import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { letterSchema } from '../../schemas/letterSchema'
import { Form, FlexEndContainer, OutlineContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { SecondaryButton } from '../common/Button'
import LetterForm from '../letters/LetterForm'
import SaveTemplateButton from './SaveTemplateButton'

const HeaderRow = styled(SpaceBetweenRowContainer)`
  min-height: 37px;
  align-items: center;
`

function formValuesFromLetterTemplate(letterTemplate) {
  return {
    name: letterTemplate?.name ?? '',
    body: letterTemplate?.body ?? ''
  }
}

function trimId(value) {
  return String(value ?? '').trim()
}

function persistedTemplateIdForRequest(propId, idFromLastSuccessfulPost) {
  const fromProps = trimId(propId)
  if (fromProps !== '') return fromProps
  return trimId(idFromLastSuccessfulPost)
}

const emptyTemplate = {
  name: '',
  body: ''
}

function EditableLetterTemplate({
  children,
  letterTemplate = emptyTemplate,
  isCreating = false,
  handleUpdateTemplate = () => {},
  handleCancelUpdateTemplate = () => {}
}) {
  const [baseline, setBaseline] = useState(() => formValuesFromLetterTemplate(letterTemplate))
  const [payloadToSave, setPayloadToSave] = useState(null)
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false)
  const [templateIdFromPostResponse, setTemplateIdFromPostResponse] = useState(null)
  const previousIsCreatingRef = useRef(isCreating)

  const formMethods = useForm({
    resolver: yupResolver(letterSchema),
    defaultValues: formValuesFromLetterTemplate(letterTemplate)
  })

  const { reset, handleSubmit } = formMethods

  useEffect(() => {
    const values = formValuesFromLetterTemplate(letterTemplate)
    setBaseline(values)
    reset(values)
  }, [letterTemplate.id, letterTemplate.name, letterTemplate.body, reset])

  useEffect(() => {
    if (trimId(letterTemplate?.id) !== '') {
      setTemplateIdFromPostResponse(null)
    }
  }, [letterTemplate.id])

  useEffect(() => {
    if (isCreating && previousIsCreatingRef.current === false) {
      setTemplateIdFromPostResponse(null)
    }
    previousIsCreatingRef.current = isCreating
  }, [isCreating])

  const onError = (errors, e) => console.error('Error', errors, e)

  function handleCancel() {
    reset(baseline)
    setIsSaveConfirmationOpen(false)
    setPayloadToSave(null)
    handleCancelUpdateTemplate()
  }

  function handleCloseSaveConfirmation() {
    setIsSaveConfirmationOpen(false)
    setPayloadToSave(null)
  }

  function onSubmit(data) {
    setPayloadToSave({ name: data.name, body: data.body })
    setIsSaveConfirmationOpen(true)
  }

  function handleSaveSuccess(saved) {
    const next = { name: saved.name, body: saved.body }
    setBaseline(next)
    reset(next)
    if (persistedTemplateIdForRequest(letterTemplate?.id, templateIdFromPostResponse) === '') {
      setTemplateIdFromPostResponse(saved.id)
    }
    handleUpdateTemplate(saved)
  }

  const templateId = persistedTemplateIdForRequest(letterTemplate?.id, templateIdFromPostResponse)

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="10px">
        <HeaderRow gap="10px">
          {children}
          <FlexEndContainer gap="5px">
            <SecondaryButton onClick={handleCancel} disabled={isSaveConfirmationOpen}>
              Cancel
            </SecondaryButton>
            <SaveTemplateButton
              payload={payloadToSave ?? emptyTemplate}
              templateId={templateId === '' ? undefined : templateId}
              open={isSaveConfirmationOpen}
              disabled={isSaveConfirmationOpen}
              onClose={handleCloseSaveConfirmation}
              onSuccess={handleSaveSuccess}
            />
          </FlexEndContainer>
        </HeaderRow>
        <OutlineContainer padding="25px 30px">
          <LetterForm disabled={isSaveConfirmationOpen} isTemplate />
        </OutlineContainer>
      </Form>
    </FormProvider>
  )
}

EditableLetterTemplate.propTypes = {
  children: PropTypes.node,
  letterTemplate: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    body: PropTypes.string
  }),
  isCreating: PropTypes.bool,
  handleUpdateTemplate: PropTypes.func,
  handleCancelUpdateTemplate: PropTypes.func
}

export default EditableLetterTemplate
